import i18next from "i18next";
import React, { useEffect, useState } from "react";
import {
  BUTTON_ADD_CONVERSION_ACTION,
  CUSTOM,
  FIELD_REQUIRED,
  INACTIVE,
  LOADING,
} from "../../i18n/keysTranslations";
import { Controller } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp } from "../../lib/redux";
import { startGetTrackingCustomEvents } from "../../actions/adsCampaigns";
import {
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  GOOGLE,
} from "../../utils/constants";
import AddIcon from "@mui/icons-material/Add";
import { openLink } from "../../utils/string";
import { getCreateConversionUrlGoogleAds } from "../../actions/getters";
import _ from "lodash";
import { getUniqueID } from "../../utils/numbers";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";

const filter = createFilterOptions();
const ID_ADD = "Create Identity";

const SelectConversionActionsGoogle = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  objective,
  appID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  sxTextField = {},
  onChangeAction = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groupedOptions, setGroupedOptions] = useState([]);
  const [customOptions, setCustomOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const isApp = objective === ADS_CAMPAIGN_TYPE_APP_OPTION;

  // Function to create a unique key for grouping based on category-origin
  const getGroupKey = (item) => {
    if (isApp) {
      return item.id;
    }
    const category = item.category || item.type || "default";
    const origin = item.origin || item.source || "default";
    return `${category}-${origin}`;
  };

  // Function to get translated group name
  const getTranslatedGroupName = (groupKey) => {
    if (isApp) {
      return groupKey;
    }
    const [category, origin] = groupKey.split("-");
    const translatedCategory = t(category) || category;
    const translatedOrigin = t(origin) || origin;
    return `${translatedCategory} - ${translatedOrigin}`;
  };

  // Function to group options by category-origin (excluding custom options)
  const groupOptionsByCategoryOrigin = (options) => {
    const groups = {};
    const customItems = [];

    options.forEach((option) => {
      if (
        isApp &&
        (option.category !== "DOWNLOAD" ||
          option.origin !== "APP" ||
          option.appID !== appID)
      ) {
        return;
      }
      // Separate custom options from regular options
      if (option.isCustom) {
        customItems.push(option);
        return;
      }

      if (isApp) {
        groups[option.id] = {
          id: option.id,
          name: option.name,
          isGroup: true,
          items: [option],
          status: ADS_CAMPAIGN_STATUS_ACTIVE,
        };
        return;
      }

      const groupKey = getGroupKey(option);

      if (!groups[groupKey]) {
        groups[groupKey] = {
          id: groupKey,
          name: getTranslatedGroupName(groupKey),
          isGroup: true,
          items: [],
          status: ADS_CAMPAIGN_STATUS_ACTIVE, // Group is active if any item is active
        };
      }

      groups[groupKey].items.push(option);

      // If any item in the group is inactive, mark the group as inactive
      if (option.status !== ADS_CAMPAIGN_STATUS_ACTIVE) {
        groups[groupKey].status = option.status;
      }
    });

    return {
      groups: Object.values(groups),
      customItems,
    };
  };

  const getInitialData = async () => {
    if (!integrationID || !adAccountID) {
      setGroupedOptions([]);
      setCustomOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let basicOptions = [];

    const response = await dispatch(
      startGetTrackingCustomEvents({
        adAccountID,
        integrationID,
        platform: GOOGLE,
        objective,
      })
    );

    if (response?.ok && response?.data?.length > 0) {
      const data = response.data;
      basicOptions = data;
    }

    // Group the options by category-origin and separate custom options
    const { groups, customItems } = groupOptionsByCategoryOrigin(basicOptions);
    setGroupedOptions(groups);
    setCustomOptions(customItems);

    setLoading(false);
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [adAccountID, integrationID]);

  const getLabelOption = (option) => {
    if (option.isGroup) {
      return option.name;
    }
    return option.name;
  };

  // Function to get all items from selected groups and custom options
  const getSelectedItemsFromGroups = (selectedGroups) => {
    const allItems = [];
    selectedGroups.forEach((group) => {
      if (group.isGroup) {
        allItems.push(...group.items);
      } else {
        allItems.push(group);
      }
    });
    return allItems;
  };

  // Function to convert individual items back to groups for display
  const convertItemsToGroupsForDisplay = (items) => {
    const groups = {};
    const customItems = [];

    items.forEach((item) => {
      // Separate custom items from regular items
      if (item.isCustom) {
        customItems.push(item);
        return;
      }

      if (isApp) {
        groups[item.id] = {
          id: item.id,
          name: item.name,
          isGroup: true,
          items: [item],
          status: ADS_CAMPAIGN_STATUS_ACTIVE,
        };
        return;
      }

      const groupKey = getGroupKey(item);

      if (!groups[groupKey]) {
        groups[groupKey] = {
          id: groupKey,
          name: getTranslatedGroupName(groupKey),
          isGroup: true,
          items: [],
          status: ADS_CAMPAIGN_STATUS_ACTIVE,
        };
      }

      groups[groupKey].items.push(item);
    });

    return [...Object.values(groups), ...customItems];
  };

  // Check if there's already a custom option selected
  const hasCustomOptionSelected = (value) => {
    return value && value.some((item) => item.isCustom);
  };

  // Combine grouped options with custom options for display
  const getAllOptions = () => {
    return [...groupedOptions, ...customOptions];
  };

  const allOptions = getAllOptions();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={[]}
      render={({ field }) => (
        <Autocomplete
          {...field}
          id={name}
          hasLabel={Boolean(label)}
          fullWidth={fullWidth}
          disabled={disabled}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={20} />
              {t(LOADING)}
            </Box>
          }
          loading={loading}
          open={open}
          multiple={true}
          value={
            field.value && field.value.length > 0
              ? convertItemsToGroupsForDisplay(field.value)
              : []
          }
          onOpen={() => {
            if (!field.value || _.isEmpty(field.value)) field.onChange([]);
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          onChange={(_, value, __, optionSelected) => {
            if (optionSelected.option?.id === ID_ADD) {
              openLink(dispatch(getCreateConversionUrlGoogleAds()));
            } else {
              // Convert selected groups to individual items for storage
              const selectedItems = getSelectedItemsFromGroups(value);
              field.onChange(selectedItems);
              onChangeAction(selectedItems);
            }
          }}
          isOptionEqualToValue={(option, value) => {
            return option?.id === value?.id;
          }}
          getOptionLabel={(option) => {
            const optionValue = allOptions.find((opt) => opt.id === option.id);
            if (optionValue) {
              return `${getLabelOption(optionValue)}`;
            }
            if (typeof option === "string") {
              return option ? t(option) : "";
            }
            return `${getLabelOption(option)}`;
          }}
          options={allOptions}
          disableClearable={true}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            if (!loading) {
              filtered.unshift({
                id: ID_ADD,
                name: t(BUTTON_ADD_CONVERSION_ACTION),
              });
            }

            return filtered;
          }}
          renderOption={(props, option) => {
            const isDisabled =
              option.status !== ADS_CAMPAIGN_STATUS_ACTIVE &&
              option.id !== ID_ADD;

            // Disable custom options if another custom option is already selected
            const isCustomDisabled =
              option.isCustom &&
              hasCustomOptionSelected(field.value) &&
              !field.value?.some((item) => item.id === option.id);
            const isCustomOption = option.isCustom;

            const finalDisabled = isDisabled || isCustomDisabled;
            const groupConversionsNames =
              option.isGroup && option.items?.length > 0
                ? option.items
                    .map((item) => item?.name)
                    .filter(Boolean)
                    .join(", ")
                : "";

            return (
              <Box
                {...props}
                key={getUniqueID()}
                onClick={(e) => {
                  if (finalDisabled) return;
                  props.onClick(e);
                }}
                onMouseOver={(e) => {
                  if (finalDisabled) return;
                  props.onMouseOver(e);
                }}
                onTouchStart={(e) => {
                  if (finalDisabled) return;
                  props.onTouchStart(e);
                }}
                component="li"
                disabled={finalDisabled}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: finalDisabled ? "default !important" : "pointer",
                }}
              >
                {option.id === ID_ADD && <AddIcon />}
                <Box
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: finalDisabled ? "text.disabled" : "inherit",
                    }}
                  >
                    {getLabelOption(option)}
                  </Typography>
                  {groupConversionsNames && (
                    <Typography variant="caption" color="text.secondary">
                      {groupConversionsNames}
                    </Typography>
                  )}
                </Box>
                {isDisabled && (
                  <Chip label={t(INACTIVE)} size="small" sx={{ mt: 0.5 }} />
                )}
                {isCustomOption && (
                  <Chip label={t(CUSTOM)} size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={getUniqueID()}
                label={getLabelOption(option)}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant={variant}
              placeholder={field?.value?.length > 0 ? "" : placeholder}
              hiddenLabel={!label}
              error={!!error}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{!!error ? error?.message : helperText}</span>
                </Box>
              }
              sx={{
                backgroundColor: "fields.background",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                ...sxTextField,
                ".MuiFormHelperText-root": {
                  ml: "0 !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default SelectConversionActionsGoogle;
